import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Fieldset,
  Field,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Radio,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { AssessedArea, RiskAssessmentError, WorkEnvironmentRisk } from '../../../api/workEnvironmentTypes';
import { useSaveRiskAssessmentMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import {
  ASSESSMENT_FIELDS,
  assessmentFieldErrors,
  assessmentForm,
  AssessmentForm,
  assessmentRequest,
  assessmentSummary,
  FieldError,
  hasSafetyRep,
  participationOptions,
  reviewTexts,
  validationErrors,
  withPlacement,
} from '../assessmentView';
import { areaName, assessedLevel, byRisk, LEVEL_COLORS } from '../riskView';
import { notifiedText } from '../reminderView';
import { AdjustmentLog } from './AdjustmentLog';
import { FormErrorSummary, TextField } from './FormParts';
import { PlacementAdjuster } from './PlacementAdjuster';
import { ReviewerNotice } from './ReviewerNotice';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Assessment = NonNullable<Extract<WorkEnvironmentRisk, { calculatedAt: string }>['assessment']>;

type Props = {
  surveyId: number;
  calculatedAt: string;
  areas: AssessedArea[];
  levels: { moderateFrom: number; highFrom: number };
  assessment: Assessment;
};

/** Required by the regulation before the team moves on: the risk in the short and long term, and in total */
export const AssessmentCard = ({ surveyId, calculatedAt, areas, levels, assessment }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const { saved } = assessment;
  // The team leader registers the assessment; the safety representative takes part and sees it
  const canChange = assessment.canChange && may('assess');
  const withSafetyRep = hasSafetyRep(assessment);
  const { usesSafetyReps } = assessment;
  const texts = reviewTexts(withSafetyRep, usesSafetyReps);
  const [editing, setEditing] = useState(!saved && canChange);
  const [form, setForm] = useState<AssessmentForm>(() => assessmentForm(saved));
  const [errors, setErrors] = useState<FieldError[]>([]);
  const save = useSaveRiskAssessmentMutation(surveyId);
  const sent = notifiedText(save.data, t, i18n.language);
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;
  const levelName = (level: string) => t(`workEnvironmentRisk.matrix.levels.${level}`);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(assessmentRequest(form, areas), {
      onSuccess: () => {
        setErrors([]);
        setEditing(false);
      },
      onError: (error) => {
        const invalid = validationErrors<RiskAssessmentError>(error);
        if (invalid) setErrors(assessmentFieldErrors(invalid, areas, t));
      },
    });
  };

  return (
    <section id="assessment" aria-labelledby="assessment-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          {canChange && (
            <span className={`ds-label ${process.eyebrow}`} data-size="sm">
              {t('workEnvironmentRisk.assessment.eyebrow')}
            </span>
          )}
          <Heading level={2} data-size="sm" id="assessment-heading">
            {t(`workEnvironmentRisk.assessment.title.${texts}`)}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentRisk.assessment.intro')}
          </Paragraph>
        </div>

        <div className={classes.reviewList}>
          <span className="ds-label" data-size="sm">
            {t('workEnvironmentRisk.assessment.reviewList')}
          </span>
          <ul>
            {byRisk(areas).map((area) => (
              <li key={area.key}>
                <span className="ds-paragraph" data-size="sm">
                  {areaName(area, t)}
                </span>
                <Tag data-size="sm" data-color={LEVEL_COLORS[assessedLevel(area)]}>
                  {levelName(assessedLevel(area))}
                </Tag>
              </li>
            ))}
          </ul>
          <Link href="#matrix" data-size="sm">
            {t('workEnvironmentRisk.assessment.seeMatrix')}
          </Link>
        </div>

        {canChange && usesSafetyReps && (
          <ReviewerNotice surveyId={surveyId} calculatedAt={calculatedAt} assessment={assessment} />
        )}

        {editing ? (
          <form onSubmit={submit} noValidate className={classes.areaBody}>
            <FormErrorSummary errors={errors} />
            {usesSafetyReps && (
              <Fieldset data-size="sm">
                <Fieldset.Legend>{t('workEnvironmentRisk.assessment.participation')}</Fieldset.Legend>
                {participationOptions(withSafetyRep).map((participation, index) => (
                  <Radio
                    key={participation}
                    id={index === 0 ? ASSESSMENT_FIELDS.participation : `assessment-participation-${participation}`}
                    name="assessment-participation"
                    value={participation}
                    label={t(
                      participation === 'NONE'
                        ? `workEnvironmentRisk.assessment.options.NONE.${texts}`
                        : `workEnvironmentRisk.assessment.options.${participation}`,
                  )}
                  checked={form.participation === participation}
                  onChange={() => setForm({ ...form, participation })}
                />
              ))}
              {errorFor(ASSESSMENT_FIELDS.participation) && (
                <ValidationMessage>{errorFor(ASSESSMENT_FIELDS.participation)}</ValidationMessage>
              )}
            </Fieldset>
            )}
            {form.participation === 'EMPLOYEE_REP' && (
              <Field data-size="sm">
                <Label htmlFor="assessment-representative-role">
                  {t('workEnvironmentRisk.assessment.representativeRole')}
                </Label>
                <Field.Description>{t('workEnvironmentRisk.assessment.representativeRoleHelp')}</Field.Description>
                <Input
                  id="assessment-representative-role"
                  value={form.representativeRole}
                  onChange={(event) => setForm({ ...form, representativeRole: event.target.value })}
                />
              </Field>
            )}
            {form.participation === 'NONE' && (
              <TextField
                id={ASSESSMENT_FIELDS.absenceReason}
                label={t(`workEnvironmentRisk.assessment.absenceReason.${texts}`)}
                help={t(`workEnvironmentRisk.assessment.absenceReasonHelp.${texts}`)}
                value={form.absenceReason}
                error={errorFor(ASSESSMENT_FIELDS.absenceReason)}
                onChange={(absenceReason) => setForm({ ...form, absenceReason })}
              />
            )}
            <TextField
              id={ASSESSMENT_FIELDS.shortLongTerm}
              label={t('workEnvironmentRisk.assessment.shortLongTerm')}
              help={t('workEnvironmentRisk.assessment.shortLongTermHelp')}
              value={form.shortLongTerm}
              error={errorFor(ASSESSMENT_FIELDS.shortLongTerm)}
              onChange={(shortLongTerm) => setForm({ ...form, shortLongTerm })}
            />
            <TextField
              id={ASSESSMENT_FIELDS.combined}
              label={t('workEnvironmentRisk.assessment.combined')}
              help={t(
                assessment.combinedRequired
                  ? 'workEnvironmentRisk.assessment.combinedRequired'
                  : 'workEnvironmentRisk.assessment.combinedOptional',
                { count: assessment.elevatedCount, from: assessment.combinedAssessmentFrom },
              )}
              value={form.combined}
              error={errorFor(ASSESSMENT_FIELDS.combined)}
              onChange={(combined) => setForm({ ...form, combined })}
            />
            <PlacementAdjuster
              areas={byRisk(areas)}
              form={form}
              levels={levels}
              errorFor={errorFor}
              onChange={(factorKey, draft) => setForm(withPlacement(form, factorKey, draft))}
            />
            {save.isError && !validationErrors(save.error) && (
              <Alert data-color="danger">
                <Paragraph>{t('workEnvironmentRisk.assessment.saveFailed')}</Paragraph>
              </Alert>
            )}
            <div className={process.linkRow}>
              <Button type="submit" variant="primary" data-size="sm" loading={save.isPending}>
                {t('workEnvironmentRisk.assessment.save')}
              </Button>
              {saved && (
                <Button type="button" variant="secondary" data-size="sm" onClick={() => setEditing(false)}>
                  {t('workEnvironmentRisk.assessment.cancel')}
                </Button>
              )}
            </div>
          </form>
        ) : !saved && assessment.canChange && !canChange ? (
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentRisk.assessment.notSavedYet')}
          </Paragraph>
        ) : (
          saved && (
            <Alert data-color="success" role="status">
              <div className={classes.areaBody}>
                <Paragraph data-size="sm">
                  {t('workEnvironmentRisk.assessment.saved', {
                    summary: assessmentSummary(saved, withSafetyRep, t),
                  })}
                </Paragraph>
                {sent && <Paragraph data-size="sm">{sent}</Paragraph>}
                {saved.log.length > 0 && <AdjustmentLog log={saved.log} areas={areas} />}
                {canChange && (
                  <div>
                    <Button
                      variant="tertiary"
                      data-size="sm"
                      onClick={() => {
                        setForm(assessmentForm(saved));
                        setEditing(true);
                      }}
                    >
                      {t('workEnvironmentRisk.assessment.edit')}
                    </Button>
                  </div>
                )}
              </div>
            </Alert>
          )
        )}
      </div>
    </section>
  );
};
